import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  StyleSheet,
  KeyboardAvoidingView,
  Keyboard,
  Platform,
  ScrollView,
  ViewStyle,
  Pressable,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { useResponsive } from '@/hooks/common/useResponsive';
import { CloseButton } from './CloseButton';
import { ModalToastOverlay } from '@/contexts/admin/ToastContext';

interface BaseModalProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  // Header options
  title?: string;
  subtitle?: string;
  showHeader?: boolean;
  centerTitle?: boolean;
  headerStyle?: ViewStyle;
  // Footer options
  footer?: React.ReactNode;
  footerStyle?: ViewStyle;
  // Behavior options
  animationType?: 'none' | 'slide' | 'fade';
  transparent?: boolean;
  avoidKeyboard?: boolean;
  scrollable?: boolean;
  scrollEnabled?: boolean;
  centerContent?: boolean;
  contentContainerStyle?: ViewStyle;
  /** Passed to Modal's onRequestClose. Defaults to onClose. Set to undefined to prevent back-button dismissal. */
  onRequestClose?: (() => void) | undefined;
  // Size options for desktop
  size?: 'default' | 'compact' | 'large';
}

/**
 * Base modal wrapper that handles common modal patterns:
 * - Safe area insets
 * - Modal container with border styling
 * - Optional header with title and close button
 * - Optional footer with divider
 * - Optional keyboard avoiding behavior
 * - Optional scrollable content
 */
export const BaseModal: React.FC<BaseModalProps> = ({
  visible,
  onClose,
  children,
  title,
  subtitle,
  showHeader = true,
  centerTitle = false,
  headerStyle,
  footer,
  footerStyle,
  animationType = 'fade',
  transparent = false,
  avoidKeyboard = false,
  scrollable = false,
  scrollEnabled = true,
  centerContent = false,
  contentContainerStyle,
  onRequestClose,
  size = 'default',
}) => {
  const requestClose = onRequestClose !== undefined ? onRequestClose : onClose;
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const isCompact = size === 'compact';
  const isLarge = size === 'large';

  // On Android, KAV inside Modal + statusBarTranslucent leaves residual space
  // after keyboard dismiss. Use Keyboard events for reliable behavior instead.
  const isAndroid = Platform.OS === 'android';
  const [androidKeyboardHeight, setAndroidKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!isAndroid || !avoidKeyboard || isDesktop) return;

    const showSub = Keyboard.addListener('keyboardDidShow', (e) => {
      setAndroidKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener('keyboardDidHide', () => {
      setAndroidKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [avoidKeyboard, isAndroid, isDesktop]);

  const renderContent = () => {
    if (scrollable) {
      return (
        <ScrollView
          style={[styles.scrollView, isDesktop && isCompact && { flexGrow: 0 }]}
          contentContainerStyle={[
            styles.scrollContent,
            centerContent && styles.scrollContentCentered,
            contentContainerStyle,
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          scrollEnabled={scrollEnabled}
        >
          {children}
        </ScrollView>
      );
    }
    return (
      <View style={[
        styles.content,
        isDesktop && isCompact && styles.contentCompact,
        centerContent && styles.contentCentered,
        contentContainerStyle,
      ]}>
        {children}
      </View>
    );
  };

  const modalContent = (
    <View style={[
      isDesktop ? styles.desktopModalContainer : styles.fullScreenContainer,
      isDesktop && isCompact && styles.desktopModalContainerCompact,
    ]}>
      <View
        style={[
          styles.modalContainer,
          isDesktop && styles.modalContainerDesktop,
          isDesktop && isCompact && styles.modalContainerCompact,
          !isDesktop && {
            paddingTop: insets.top,
            paddingBottom: footer ? 0 : insets.bottom,
          },
        ]}
      >
        {/* Header */}
        {showHeader && (
          <View style={[styles.header, isDesktop && styles.headerDesktop, headerStyle]}>
            {centerTitle && <View style={styles.headerCenterSpacer} />}
            {title ? (
              <View style={subtitle ? styles.headerTitleContainer : undefined}>
                <Text style={[styles.headerTitle, centerTitle && styles.headerTitleCentered]}>{title}</Text>
                {subtitle && <Text style={styles.headerSubtitle}>{subtitle}</Text>}
              </View>
            ) : (
              <View style={styles.headerSpacer} />
            )}
            <CloseButton onPress={onClose} />
          </View>
        )}

        {/* Content */}
        {renderContent()}

        {/* Footer */}
        {footer && (
          <View style={[styles.footer, { paddingBottom: isDesktop ? 24 : (insets.bottom || 16) }, footerStyle]}>
            {footer}
          </View>
        )}
      </View>
    </View>
  );

  // Desktop: Render as centered dialog with backdrop
  if (isDesktop) {
    return (
      <Modal
        visible={visible}
        animationType="fade"
        transparent={true}
        onRequestClose={requestClose}
      >
        <View style={styles.desktopOverlay}>
          <Pressable style={styles.desktopBackdrop} onPress={onClose} />
          <View style={[
            styles.desktopDialog,
            isCompact && styles.desktopDialogCompact,
            isLarge && styles.desktopDialogLarge,
          ]}>
            {avoidKeyboard ? (
              <KeyboardAvoidingView
                style={styles.keyboardAvoidingView}
                behavior="padding"
                keyboardVerticalOffset={0}
              >
                {modalContent}
              </KeyboardAvoidingView>
            ) : (
              modalContent
            )}
          </View>
        </View>
        <ModalToastOverlay />
      </Modal>
    );
  }

  // Mobile/Tablet: Render as fullscreen modal
  return (
    <Modal
      visible={visible}
      animationType={animationType}
      transparent={transparent}
      statusBarTranslucent
      onRequestClose={requestClose}
    >
      {avoidKeyboard && !isAndroid ? (
        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior="padding"
          keyboardVerticalOffset={0}
        >
          {modalContent}
        </KeyboardAvoidingView>
      ) : (
        <View style={[
          styles.keyboardAvoidingView,
          isAndroid && avoidKeyboard && androidKeyboardHeight > 0 && { paddingBottom: androidKeyboardHeight + 8 },
        ]}>
          {modalContent}
        </View>
      )}
      <ModalToastOverlay />
    </Modal>
  );
};

/**
 * Pass these as footer/footerStyle to BaseModal when the content component
 * renders its own footer (e.g. CreditCardFormContent, ACHFormContent).
 * Prevents BaseModal from adding a duplicate safe-area bottom padding.
 */
export const EMPTY_MODAL_FOOTER = <></>;
export const EMPTY_MODAL_FOOTER_STYLE = { borderTopWidth: 0, paddingTop: 0, paddingBottom: 0, paddingHorizontal: 0 };

const styles = StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
  },
  fullScreenContainer: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.background,
  },
  // Desktop overlay and dialog
  desktopOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  desktopBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  desktopDialog: {
    width: '90%',
    maxWidth: 480,
    maxHeight: '85%',
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
  },
  desktopDialogCompact: {
    maxWidth: 400,
    maxHeight: undefined, // Auto-height for compact
  },
  desktopDialogLarge: {
    maxWidth: 560,
    maxHeight: '90%',
  },
  desktopModalContainer: {
    flex: 1,
  },
  desktopModalContainerCompact: {
    flex: undefined, // Don't stretch for compact
  },
  modalContainer: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.modalBorder,
    borderRadius: 8,
    margin: 1,
    overflow: 'hidden',
  },
  modalContainerDesktop: {
    borderRadius: 20,
    margin: 0,
  },
  modalContainerCompact: {
    flex: undefined, // Auto-height
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.dividerGray,
  },
  headerDesktop: {
    paddingHorizontal: 32,
    paddingVertical: 24,
  },
  headerSpacer: {
    flex: 1,
  },
  headerCenterSpacer: {
    width: 40,
  },
  headerTitleContainer: {
    flex: 1,
    gap: 4,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 27,
  },
  headerTitleCentered: {
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 12,
    fontWeight: '300',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  content: {
    flex: 1,
  },
  contentCompact: {
    flex: undefined,
    paddingVertical: 32,
  },
  contentCentered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  scrollContentCentered: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: MARKETPLACE_COLORS.dividerGray,
    paddingHorizontal: 24,
    paddingTop: 16,
  },
});

